import { Array, Option, Schema as Schema_ } from '@blade/std-lib'
import * as InstantTradeBuyButton from '@root/entities/instant-trade-buy-button/instant-trade-buy-button'
import * as InstantTradeSellButton from '@root/entities/instant-trade-sell-button/instant-trade-sell-button'
import { Network } from '@root/entities/network'
import { ReferralCodeStatusSchema, UserShort } from '@root/entities/user-short'

import * as Session from './session'
import { TelegramId, TelegramIdSchema } from './telegram-id'
import { UserStatus, UserStatusSchema } from './user-status'

import type { UserApi } from '@root/shared/api'
import type { AvatarsHost } from '@root/shared/models'

export type UserDto = UserApi.UserDto

const LinkedAccountsSchema = Schema_.Struct({
  twitter: Schema_.UndefinedOrFromOptional(
    Schema_.Struct({
      id: Schema_.String,
      username: Schema_.String,
    }),
  ),
})

export const Schema = Schema_.Struct({
  ...UserShort.Schema.fields,
  tgId: Schema_.UndefinedOrFromOptional(TelegramIdSchema),
  status: UserStatusSchema,
  googleAuthEnabled: Schema_.Boolean,
  sessions: Schema_.Array(Session.Schema),
  otpEnabled: Schema_.Boolean,
  otpQuickLoginEnabled: Schema_.Boolean,
  isIncognito: Schema_.Boolean,
  /**
   * Account ids, not people: the follow button asks "am I following this
   * one" and the header counts them. The lists themselves are their own
   * read, because they are pages of people and this arrives on every poll.
   */
  followers: Schema_.Array(Schema_.String),
  following: Schema_.Array(Schema_.String),
  preferredNetwork: Schema_.mutableKey(Network.Schema),
  referralCodeStatus: Schema_.UndefinedOrFromOptional(ReferralCodeStatusSchema),
  instantTradeButtons: Schema_.Struct({
    buyButtons: Schema_.Array(InstantTradeBuyButton.Schema),
    sellButtons: Schema_.Array(InstantTradeSellButton.Schema),
  }),
  linkedAccounts: Schema_.UndefinedOrFromOptional(LinkedAccountsSchema),
})

export interface User extends Schema_.Schema.Type<typeof Schema> {}
export type Encoded = Schema_.Codec.Encoded<typeof Schema>

export const isAdmin = (user: User) => user.status === UserStatus.Admin

export const fromDto = (
  dto: UserApi.UserDto,
  avatarsHost: AvatarsHost,
): User => {
  return {
    ...UserShort.fromDto(dto, avatarsHost),
    avatarSrc: dto.avatar
      ? `${avatarsHost}/${dto.avatar}`
      : // NOTE: Wait for BE support and check
        `${avatarsHost}/${dto.delugeId}.jpg`,
    tgId: dto.tgId ? TelegramId(dto.tgId) : undefined,
    status: dto.status,
    googleAuthEnabled: dto.googleAuthEnabled,
    sessions: Array.sort(Session.cmpByLastLoginDesc)(
      dto.sessionInfos?.map(Session.fromDto) ?? [],
    ),
    otpEnabled: dto.otpEnabled,
    otpQuickLoginEnabled: dto.otpQuickLogin,
    preferredNetwork: Network.fromDto(dto.network),
    referralCodeStatus: dto.referralCodeStatus,
    isIncognito: dto.isIncognito,
    followers: dto.followers,
    following: dto.following,
    linkedAccounts: dto.linkedAccounts
      ? {
          twitter: dto.linkedAccounts.twitter,
        }
      : undefined,

    instantTradeButtons: {
      buyButtons: dto.instantTradeButtons.buyButtons.map((dto) =>
        InstantTradeBuyButton.fromDto(dto),
      ),
      sellButtons: dto.instantTradeButtons.sellButtons.map((dto) =>
        InstantTradeSellButton.fromDto(dto),
      ),
    },
  }
}

export const fullName = UserShort.fullName

export const initials = UserShort.initials

export const eqInstantTradeButtons = (
  a: User['instantTradeButtons'],
  b: User['instantTradeButtons'],
) => {
  return (
    eqBuyButtonArray(a.buyButtons, b.buyButtons) &&
    eqSellButtonArray(a.sellButtons, b.sellButtons)
  )
}

export const eqBuyButtonArray = Array.makeEquivalence(InstantTradeBuyButton.eq)
export const eqSellButtonArray = Array.makeEquivalence(
  InstantTradeSellButton.eq,
)

export const encode = (user: User) => {
  return Option.getOrUndefined(Schema_.encodeOption(Schema)(user))
}

export const decode = (encoded: Encoded) => {
  return Option.getOrUndefined(Schema_.decodeOption(Schema)(encoded))
}
